/* PDP gallery — thumbnails, dots, arrows, zoom, swipe and variant-image swap.

   Ported from prototype/pages/product-detail/product-detail.js (initialiseGallery)
   to work with real Shopify product media. The DOM contract is the same: the
   section renders one <img> per medium inside .pdp-gallery__stage, one thumb button
   per medium, and one dot <i> per medium. Only the active medium's img is visible;
   selecting swaps by data-index.

   Variant selection fires a custom event 'pdp:variant-change' with the new variant
   image URL; the gallery scrolls to the matching medium (or stays if none matches).
*/

(() => {
  if (window.CaratKinPdpGallery) return;
  window.CaratKinPdpGallery = true;

  // The thumbnails carry data-gallery-src rendered through image_url (…?width=1200),
  // while a variant's featured_image.src is the raw CDN URL (…?v=123). Same file,
  // different query string, so compare by path alone — otherwise the swap never
  // matches and the gallery ignores the variant change.
  const imageKey = (u) => {
    if (!u) return '';
    const path = String(u).split('?')[0];
    // Drop any Shopify size suffix (…_1200x.jpg) so transformed and raw URLs align.
    return path.replace(/_(\d+)x(\d+)?(?=\.[a-z]+$)/i, '');
  };

  function init(root) {
    const stage = root.querySelector('.pdp-gallery__stage');
    const mainImage = root.querySelector('[data-gallery-main]');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const dots = [...root.querySelectorAll('.pdp-gallery__dots i')];
    if (!stage || !mainImage || !thumbs.length) return;

    const images = [...root.querySelectorAll('[data-gallery-src]')];
    const strip = root.querySelector('.pdp-gallery__thumbs');
    let current = 0;

    /* ---- Loading: right size, ahead of time ------------------------------
       Ask Shopify's CDN for the width the stage actually needs (retina capped
       at 2x) rather than a fixed 1200px, and fetch + decode images before
       they are shown, so a switch is an instant swap instead of a download. */
    const wanted = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      return Math.min(1600, Math.ceil(((stage.clientWidth || 600) * dpr) / 100) * 100);
    };
    const sized = (src) => {
      try {
        const url = new URL(src, window.location.href);
        url.searchParams.set('width', String(wanted()));
        return url.toString();
      } catch {
        return src;
      }
    };
    const loads = new Map(); // url -> Promise that settles once decoded

    function preload(index) {
      const i = (index + images.length) % images.length;
      const src = images[i]?.dataset.gallerySrc;
      if (!src) return Promise.resolve(null);
      const url = sized(src);
      if (!loads.has(url)) {
        const img = new Image();
        img.decoding = 'async';
        // Settle on whichever comes first: downloaded, decoded, failed, or 4s.
        // decode() alone is not enough — browsers hold it until a background
        // tab is visible again, which would leave the stage stuck mid-switch.
        const ready = new Promise((resolve) => {
          let settled = false;
          const done = () => { if (!settled) { settled = true; resolve(); } };
          img.onload = done;
          img.onerror = done;
          window.setTimeout(done, 4000);
          img.src = url;
          if (img.decode) img.decode().then(done, done);
        });
        loads.set(url, ready.then(() => url));
      }
      return loads.get(url);
    }

    /* Keep the selected thumbnail visible in the one-row strip. */
    function revealThumb(i) {
      const thumb = thumbs[i];
      if (!strip || !thumb || strip.scrollWidth <= strip.clientWidth) return;
      const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }

    let ticket = 0;

    function selectImage(index) {
      current = (index + thumbs.length) % thumbs.length;
      const mine = ++ticket;
      stage.classList.remove('is-zoomed');
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === current));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      revealThumb(current);

      // Only dim the stage if the image is genuinely still loading.
      const slow = window.setTimeout(() => {
        if (mine === ticket) stage.classList.add('is-changing');
      }, 120);

      preload(current).then((url) => {
        window.clearTimeout(slow);
        if (mine !== ticket) return; // a newer choice has already won
        if (url) {
          // The main image carries a srcset for its first paint. The browser would
          // keep choosing from it and ignore a new src, so clear it first.
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          mainImage.src = url;
          mainImage.alt = thumbs[current]?.querySelector('img')?.alt || '';
        }
        stage.classList.remove('is-changing');
        // Warm the neighbours so the next arrow tap or swipe is instant too.
        preload(current + 1);
        preload(current - 1);
      });
    }

    thumbs.forEach((thumb) => {
      const i = Number(thumb.dataset.galleryThumb);
      thumb.addEventListener('click', () => selectImage(i));
      // Start loading on intent: hover, focus, or the first touch.
      ['pointerenter', 'focus', 'touchstart'].forEach((type) =>
        thumb.addEventListener(type, () => preload(i), { passive: true })
      );
    });

    // After the page settles, warm the images either side of the first one.
    const warm = () => { preload(1); preload(images.length - 1); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2500 });
    else window.setTimeout(warm, 1200);

    /* ---- Drag the strip with a mouse (touch and trackpads scroll natively) */
    if (strip) {
      let dragging = null;
      strip.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || strip.scrollWidth <= strip.clientWidth) return;
        dragging = { x: e.clientX, left: strip.scrollLeft, moved: false };
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - dragging.x;
        if (!dragging.moved && Math.abs(dx) > 5) {
          dragging.moved = true;
          strip.classList.add('is-dragging');
        }
        if (dragging.moved) strip.scrollLeft = dragging.left - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        const moved = dragging.moved;
        dragging = null;
        strip.classList.remove('is-dragging');
        // A drag must not also count as a click on the thumbnail under the pointer.
        // The click (if any) fires before timers run, so drop the guard right after
        // — otherwise it could swallow the shopper's next real tap.
        if (moved) {
          const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
          strip.addEventListener('click', swallow, true);
          window.setTimeout(() => strip.removeEventListener('click', swallow, true), 0);
        }
      });
    }

    root.querySelector('[data-gallery-previous]')?.addEventListener('click', () => selectImage(current - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => selectImage(current + 1));
    root.querySelector('.pdp-gallery__zoom')?.addEventListener('click', () => {
      stage.classList.toggle('is-zoomed');
    });

    // Touch swipe
    let touchStart = 0;
    stage.addEventListener('touchstart', (e) => { touchStart = e.changedTouches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      const dist = e.changedTouches[0].clientX - touchStart;
      if (Math.abs(dist) > 45) selectImage(current + (dist < 0 ? 1 : -1));
    }, { passive: true });

    // Listen for variant change to scroll gallery to matching image
    document.addEventListener('pdp:variant-change', (e) => {
      const url = e.detail?.imageUrl;
      if (!url) return;
      const key = imageKey(url);
      const matchIndex = images.findIndex((img) => imageKey(img.dataset.gallerySrc) === key);
      if (matchIndex >= 0 && matchIndex !== current) selectImage(matchIndex);
    });
  }

  function mount() {
    const section = document.querySelector('[data-pdp-gallery]');
    if (section) init(section);
  }

  mount();
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-pdp-gallery]')) init(e.target);
  });
})();
